import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { adminNotificationService } from '../../../services/adminNotifications';
import type { AdminNotification } from '../../../types/adminNotification';

interface NotificationStatisticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  notification: AdminNotification;
}

export const NotificationStatisticsModal: React.FC<
  NotificationStatisticsModalProps
> = ({ isOpen, onClose, notification }) => {
  const { t } = useTranslation('admin');
  const { data: stats, isLoading } = useQuery({
    queryKey: ['notificationStatistics', notification.id],
    queryFn: () => adminNotificationService.getStatistics(notification.id),
    enabled: isOpen,
  });

  if (!isOpen) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'sending':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-4xl my-8 mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">{t('notifications.statistics_modal.title')}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {notification.title.en}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-gray-500">{t('notifications.statistics_modal.loading')}</p>
            </div>
          ) : stats ? (
            <div className="space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(
                    stats.status
                  )}`}
                >
                  {t(`notifications.status.${stats.status}`)}
                </span>
                {stats.sent_at && (
                  <span className="text-sm text-gray-500">
                    {t('notifications.statistics_modal.details.sent')}: {new Date(stats.sent_at).toLocaleString()}
                  </span>
                )}
                {stats.scheduled_at && !stats.sent_at && (
                  <span className="text-sm text-gray-500">
                    {t('notifications.statistics_modal.details.scheduled')}: {new Date(stats.scheduled_at).toLocaleString()}
                  </span>
                )}
              </div>

              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Targeted */}
                <div className="card bg-blue-50 border-blue-200">
                  <div className="text-sm text-blue-600 font-medium">
                    {t('notifications.statistics_modal.overview.total_targeted')}
                  </div>
                  <div className="text-3xl font-bold text-blue-900 mt-2">
                    {stats.total_targeted || notification.recipients_count || 0}
                  </div>
                </div>

                {/* Total Sent */}
                <div className="card bg-green-50 border-green-200">
                  <div className="text-sm text-green-600 font-medium">
                    {t('notifications.statistics_modal.overview.successfully_sent')}
                  </div>
                  <div className="text-3xl font-bold text-green-900 mt-2">
                    {stats.total_sent ||
                      stats.send_statistics?.sent ||
                      notification.recipients_count ||
                      0}
                  </div>
                </div>

                {/* Total Read */}
                {stats.total_read !== undefined && (
                  <div className="card bg-purple-50 border-purple-200">
                    <div className="text-sm text-purple-600 font-medium">
                      {t('notifications.statistics_modal.overview.total_read')}
                    </div>
                    <div className="text-3xl font-bold text-purple-900 mt-2">
                      {stats.total_read}
                    </div>
                  </div>
                )}

                {/* Read Rate */}
                {stats.read_rate !== undefined && (
                  <div className="card bg-indigo-50 border-indigo-200">
                    <div className="text-sm text-indigo-600 font-medium">
                      {t('notifications.statistics_modal.overview.read_rate')}
                    </div>
                    <div className="text-3xl font-bold text-indigo-900 mt-2">
                      {stats.read_rate.toFixed(1)}%
                    </div>
                  </div>
                )}
              </div>

              {/* Send Statistics */}
              {stats.send_statistics && (
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">
                    {t('notifications.statistics_modal.delivery_report')}
                  </h3>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-gray-600">{t('notifications.statistics_modal.send_statistics.total')}</div>
                      <div className="text-2xl font-bold">
                        {stats.send_statistics.total}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-green-600">{t('notifications.statistics_modal.send_statistics.sent')}</div>
                      <div className="text-2xl font-bold text-green-700">
                        {stats.send_statistics.sent}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-red-600">{t('notifications.statistics_modal.send_statistics.failed')}</div>
                      <div className="text-2xl font-bold text-red-700">
                        {stats.send_statistics.failed}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div
                      className="bg-green-500 h-full transition-all duration-300"
                      style={{
                        width: `${
                          (stats.send_statistics.sent /
                            stats.send_statistics.total) *
                          100
                        }%`,
                      }}
                    ></div>
                  </div>

                  {/* Errors */}
                  {stats.send_statistics.errors &&
                    stats.send_statistics.errors.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold text-red-600 mb-2">
                          {t('notifications.statistics_modal.delivery_errors', { count: stats.send_statistics.errors.length })}
                        </h4>
                        <div className="bg-red-50 rounded p-3 max-h-40 overflow-y-auto">
                          {stats.send_statistics.errors.map((error, index) => (
                            <div
                              key={index}
                              className="text-xs text-red-700 mb-1"
                            >
                              <strong>{t('notifications.statistics_modal.error_user', { userId: error.user_id })}:</strong>{' '}
                              {error.error}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* By Locale Breakdown */}
              {stats.by_locale && stats.by_locale.length > 0 && (
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">
                    {t('notifications.statistics_modal.language_distribution')}
                  </h3>
                  <div className="space-y-3">
                    {stats.by_locale.map((localeData) => {
                      const total =
                        stats.total_sent || notification.recipients_count || 1;
                      const percentage = (localeData.count / total) * 100;

                      return (
                        <div key={localeData.locale}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium">
                              {t(`notifications.statistics_modal.languages.${localeData.locale}`, localeData.locale.toUpperCase())}
                            </span>
                            <span className="text-sm text-gray-600">
                              {localeData.count} ({percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-indigo-600 h-full rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Additional Info */}
              <div className="card bg-gray-50">
                <h3 className="text-lg font-semibold mb-3">
                  {t('notifications.statistics_modal.notification_details')}
                </h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-gray-600">{t('notifications.statistics_modal.details.type')}</dt>
                    <dd className="font-medium capitalize">
                      {t(`notifications.types.${notification.type}`)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-600">{t('notifications.statistics_modal.details.channels')}</dt>
                    <dd className="font-medium">
                      {notification.type === 'security' ||
                      notification.type === 'maintenance'
                        ? t('notifications.statistics_modal.details.channel_email')
                        : t('notifications.statistics_modal.details.channel_inapp')}
                    </dd>
                  </div>
                  {notification.action_url && (
                    <div>
                      <dt className="text-gray-600">{t('notifications.statistics_modal.details.action_url')}</dt>
                      <dd className="font-medium truncate">
                        <a
                          href={notification.action_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:underline"
                        >
                          {notification.action_url}
                        </a>
                      </dd>
                    </div>
                  )}
                  {notification.expires_after_days && (
                    <div>
                      <dt className="text-gray-600">{t('notifications.statistics_modal.details.expires_after')}</dt>
                      <dd className="font-medium">
                        {t('notifications.statistics_modal.details.days', { count: notification.expires_after_days })}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-gray-600">{t('notifications.statistics_modal.details.created')}</dt>
                    <dd className="font-medium">
                      {new Date(notification.created_at).toLocaleString()}
                    </dd>
                  </div>
                  {notification.creator && (
                    <div>
                      <dt className="text-gray-600">{t('notifications.statistics_modal.details.created_by')}</dt>
                      <dd className="font-medium">{notification.creator.name}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {t('notifications.statistics_modal.error')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button onClick={onClose} className="btn btn-primary">
            {t('notifications.statistics_modal.close_button')}
          </button>
        </div>
      </div>
    </div>
  );
};
